/** @format */
/**
 * HolidayCalendar.tsx
 *
 * Gestione festività — pagina dedicata.
 * Usato da ViewHoliday.tsx.
 *
 * Backend invariato: usa gli stessi endpoint e la stessa logica
 * del controller HolidayController esistente.
 *
 * Verificare in API_ENDPOINTS:
 *   HOLIDAY_CALENDAR : endpoint GET/POST  (es. /holidays)
 *   OFFICES          : endpoint GET lista sedi (es. /offices)
 */

import React, { useState, useEffect, useMemo } from "react";
import {
    Row, Col, Calendar, Form, Skeleton,
    Tooltip, Avatar, Divider, Pagination, Popconfirm, Checkbox,
} from "antd";
import { LeftCircleOutlined, RightCircleOutlined, DeleteOutlined } from "@ant-design/icons";
import moment from "moment";
import styled from "styled-components";
import type { AxiosError } from "axios";
import { BaseColor } from "../../../theme/maps/Colors";
import { UICard } from "../../ui/Card";
import { UITitle } from "../../ui/Title";
import { UIText } from "../../ui/Text";
import { UIButton } from "../../ui/Button";
import { UIInput } from "../../ui/Input";
import { UISelect } from "../../ui/Select";
import { CustomRangePicker } from "../../layout/SideDialogs/RequestCreate";
import { API_ENDPOINTS } from "../../../constants/api";
import apiClient from "../../../utils/apiClient";
import { showNotification } from "../../../utils/showNotification";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface THoliday {
    id: number;
    description: string;
    date: string;
    offices: number[];
    ricorsivo: number;
}

export interface TOffice {
    id: number;
    name: string;
}

interface THolidayFormValues {
    description: string;
    is_recurrent: boolean;
    date: [moment.Moment, moment.Moment];
    office_ids: (number | string)[];
}

// ─── Utils ────────────────────────────────────────────────────────────────────

/** Mappa data ISO → THoliday (una entry per data, come restituisce il backend). */
function buildHolidayMap(holidays: THoliday[]): Record<string, THoliday> {
    return holidays.reduce<Record<string, THoliday>>((acc, h) => {
        acc[h.date] = h;
        return acc;
    }, {});
}

// ─── Component ────────────────────────────────────────────────────────────────

export const HolidayCalendar: React.FC = () => {
    const [holidays,     setHolidays]     = useState<THoliday[]>([]);
    const [offices,      setOffices]      = useState<TOffice[]>([]);
    const [isLoading,    setIsLoading]    = useState(false);
    const [selectedDate, setSelectedDate] = useState<string | undefined>(undefined);
    const [currentPage,  setCurrentPage]  = useState(1);
    const [form]                          = Form.useForm();

    const PAGE_SIZE = 10;

    useEffect(() => {
        loadHolidays();
        loadOffices();
    }, []);

    const loadHolidays = () => {
        setIsLoading(true);
        apiClient
            .get(API_ENDPOINTS.HOLIDAY_CALENDAR)
            .then(res => { setHolidays(res.data); setIsLoading(false); })
            .catch(() => {
                showNotification("error", "Errore", "Impossibile caricare le festività");
                setIsLoading(false);
            });
    };

    const loadOffices = () => {
        apiClient
            .get(API_ENDPOINTS.LOCATIONS)
            .then(res => { setOffices(res.data.data ?? res.data); })
            .catch(() => {
                showNotification("error", "Errore", "Impossibile caricare le sedi");
            });
    };

    const holidayMap = useMemo(() => buildHolidayMap(holidays), [holidays]);

    const getHolidayByDate = (ds: string): THoliday | undefined => holidayMap[ds];

    // ── Save ──────────────────────────────────────────────────────────────────
    const handleAdd = async (values: THolidayFormValues) => {
        setIsLoading(true);
        const payload = {
            ...values,
            date_from:  values.date[0].format("YYYY-MM-DD"),
            date_to:    values.date[1].format("YYYY-MM-DD"),
            office_ids: values.office_ids.some(o => o === "all") ? [] : values.office_ids,
        };
        apiClient
            .post(API_ENDPOINTS.HOLIDAY_CALENDAR, payload)
            .then(() => {
                showNotification("success", "Successo", "Festività creata con successo");
                form.resetFields();
                setCurrentPage(1);
                loadHolidays();
            })
            .catch((err: AxiosError<{ message?: string }>) => {
                showNotification(
                    "error",
                    "Oops..",
                    err.response?.data?.message || "Errore durante il salvataggio"
                );
                setIsLoading(false);
            });
    };

    // ── Delete ────────────────────────────────────────────────────────────────
    const handleDelete = async (id: number) => {
        try {
            await apiClient.delete(`${API_ENDPOINTS.HOLIDAY_CALENDAR}/${id}`);
            setSelectedDate(undefined);
            showNotification("success", "Eliminata", "Festività rimossa correttamente");
            loadHolidays();
        } catch {
            showNotification("error", "Errore", "Impossibile eliminare la festività");
        }
    };

    const selectedHoliday = selectedDate ? getHolidayByDate(selectedDate) : undefined;

    const officeNames = (officeIds: number[]) =>
        officeIds
            .map(id => offices.find(o => o.id === id)?.name)
            .filter(Boolean)
            .join(", ") || "Tutte le sedi";

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <Row gutter={[24, 24]}>

            {/* ──────────────── Calendario ──────────────────────────────────── */}
            <Col xs={24} lg={14}>
                <UICard style={{ minHeight: "460px" }}>
                    {isLoading ? (
                        <Skeleton active />
                    ) : (
                        <CustomHolidayCalendar
                            fullscreen={false}
                            //@ts-ignore
                            headerRender={({ value, onChange }) => {
                                const cur = moment(value.clone());
                                return (
                                    <Row justify="center" style={{ padding: "8px 0 12px" }}>
                                        <span
                                            className="back-arrow"
                                            onClick={() => onChange(cur.clone().subtract(1, "month"))}
                                        >
                                            <LeftCircleOutlined />
                                        </span>
                                        <span
                                            className="month-name"
                                            data-date={cur.format("YYYY-MM-DD")}
                                        >
                                            {cur.format("MMMM YYYY")}
                                        </span>
                                        <span
                                            className="next-arrow"
                                            onClick={() => onChange(cur.clone().add(1, "month"))}
                                        >
                                            <RightCircleOutlined />
                                        </span>
                                    </Row>
                                );
                            }}
                            dateFullCellRender={(value: moment.Moment) => {
                                const ds      = value.format("YYYY-MM-DD");
                                const holiday = getHolidayByDate(ds);
                                const isSel   = ds === selectedDate;

                                if (!holiday) {
                                    return <DayCell $selected={isSel}>{value.format("DD")}</DayCell>;
                                }

                                return (
                                    <Tooltip title={holiday.description}>
                                        <span
                                            style={{ cursor: "pointer" }}
                                            onClick={() => setSelectedDate(isSel ? undefined : ds)}
                                        >
                                            <Avatar
                                                style={{
                                                    backgroundColor: isSel ? "#2d4fd4" : BaseColor,
                                                }}
                                            >
                                                {value.format("DD")}
                                            </Avatar>
                                        </span>
                                    </Tooltip>
                                );
                            }}
                        />
                    )}
                </UICard>
            </Col>

            {/* ──────────────── Pannello destro ─────────────────────────────── */}
            <Col xs={24} lg={10}>

                {/* Form aggiunta — sempre visibile */}
                <UICard style={{ marginBottom: "16px" }}>
                    <UITitle level={4} color={BaseColor} style={{ marginBottom: "16px" }}>
                        Aggiungi festività
                    </UITitle>

                    <Form layout="vertical" form={form} onFinish={handleAdd}>
                        <Row gutter={[16, 0]}>
                            <Col span={16}>
                                <Form.Item
                                    name="description"
                                    label="Descrizione"
                                    rules={[{
                                        required: true,
                                        message: "Inserisci il nome della festività",
                                    }]}
                                >
                                    <UIInput placeholder="Es. Natale, Ferragosto…" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    name="is_recurrent"
                                    label="Ricorrente?"
                                    valuePropName="checked"
                                >
                                    <StyledCheckbox>Sì</StyledCheckbox>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item
                            name="date"
                            label="Periodo"
                            rules={[{
                                required: true,
                                message: "Seleziona il periodo della festività",
                            }]}
                        >
                            <CustomRangePicker
                                //@ts-ignore
                                showTime={false}
                                style={{ width: "100%" }}
                                format="DD/MM/YYYY"
                            />
                        </Form.Item>

                        <Form.Item
                            name="office_ids"
                            label="Sedi"
                            rules={[{
                                required: true,
                                message: "Seleziona almeno una sede",
                            }]}
                        >
                            <UISelect
                                mode="multiple"
                                placeholder="Seleziona le sedi"
                                style={{ width: "100%", minHeight: "60px" }}
                            >
                                <UISelect.Option key="all" value="all">
                                    Tutte
                                </UISelect.Option>
                                {offices.map(o => (
                                    <UISelect.Option key={o.id} value={o.id}>
                                        {o.name}
                                    </UISelect.Option>
                                ))}
                            </UISelect>
                        </Form.Item>

                        <Row justify="end">
                            <Col>
                                <UIButton type="primary" htmlType="submit" loading={isLoading}>
                                    Aggiungi
                                </UIButton>
                            </Col>
                        </Row>
                    </Form>
                </UICard>

                {/* Dettaglio giorno — visibile solo se data selezionata */}
                {!!selectedDate && !!selectedHoliday && (
                    <UICard>
                        <UITitle level={4} color={BaseColor} style={{ marginBottom: "4px" }}>
                            {selectedHoliday.description}
                        </UITitle>
                        <UIText style={{ color: "#9ca3af", fontSize: "13px" }}>
                            {moment(selectedHoliday.date).format("DD MMMM YYYY")}
                        </UIText>

                        <Divider style={{ margin: "12px 0" }} />

                        <DetailRow>
                            <UIText style={{ color: "#6b7280", minWidth: "80px" }}>Ricorrente</UIText>
                            <UIText style={{ fontWeight: 500 }}>
                                {selectedHoliday.ricorsivo ? "Sì" : "No"}
                            </UIText>
                        </DetailRow>

                        <DetailRow>
                            <UIText style={{ color: "#6b7280", minWidth: "80px" }}>Sedi</UIText>
                            <UIText style={{ fontWeight: 500 }}>
                                {selectedHoliday.offices?.length
                                    ? officeNames(selectedHoliday.offices)
                                    : "Tutte le sedi"
                                }
                            </UIText>
                        </DetailRow>

                        <Divider style={{ margin: "12px 0" }} />

                        <Popconfirm
                            title="Eliminare questa festività e tutte le date collegate?"
                            okText="Sì, elimina"
                            cancelText="Annulla"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => handleDelete(selectedHoliday.id)}
                        >
                            <UIButton
                                icon={<DeleteOutlined />}
                                loading={isLoading}
                                style={{
                                    borderRadius: "7px",
                                    backgroundColor: "#e00102",
                                    borderColor: "#e00102",
                                    color: "white",
                                }}
                            >
                                Elimina festività
                            </UIButton>
                        </Popconfirm>
                    </UICard>
                )}
            </Col>

            {/* ──────────────── Lista completa ──────────────────────────────── */}
            <Col xs={24}>
                <UICard>
                    <UITitle level={4} color={BaseColor} style={{ marginBottom: "16px" }}>
                        Tutte le festività
                    </UITitle>

                    {isLoading ? (
                        <Skeleton active />
                    ) : holidays.length === 0 ? (
                        <UIText style={{ color: "#9ca3af", fontStyle: "italic" }}>
                            Nessuna festività presente.
                        </UIText>
                    ) : (
                        <>
                            {holidays
                                .slice()
                                .sort((a, b) => a.date.localeCompare(b.date))
                                .slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
                                .map(h => (
                                    <AllHolidaysRow key={h.id}>
                                        <Col flex="110px">
                                            <UIText style={{ fontSize: "13px", color: "#6b7280" }}>
                                                {moment(h.date).format("DD/MM/YYYY")}
                                            </UIText>
                                        </Col>
                                        <Col flex="auto" style={{ minWidth: 0 }}>
                                            <UIText style={{ fontWeight: 500 }}>
                                                {h.description}
                                            </UIText>
                                            <UIText style={{ fontSize: "12px", color: "#9ca3af", display: "block" }}>
                                                {h.offices?.length
                                                    ? officeNames(h.offices)
                                                    : "Tutte le sedi"
                                                }
                                                {!!h.ricorsivo && (
                                                    <RecurringBadge>Ricorrente</RecurringBadge>
                                                )}
                                            </UIText>
                                        </Col>
                                        <Col flex="none">
                                            <Popconfirm
                                                title="Eliminare questa festività e tutte le date collegate?"
                                                okText="Sì, elimina"
                                                cancelText="Annulla"
                                                okButtonProps={{ danger: true }}
                                                onConfirm={() => handleDelete(h.id)}
                                            >
                                                <UIButton
                                                    icon={<DeleteOutlined />}
                                                    loading={isLoading}
                                                    style={{
                                                        borderRadius: "7px",
                                                        backgroundColor: "#e00102",
                                                        borderColor: "#e00102",
                                                        color: "white",
                                                    }}
                                                />
                                            </Popconfirm>
                                        </Col>
                                    </AllHolidaysRow>
                                ))
                            }
                            {holidays.length > PAGE_SIZE && (
                                <Row justify="end" style={{ marginTop: "16px" }}>
                                    <Pagination
                                        current={currentPage}
                                        pageSize={PAGE_SIZE}
                                        total={holidays.length}
                                        onChange={setCurrentPage}
                                        showSizeChanger={false}
                                    />
                                </Row>
                            )}
                        </>
                    )}
                </UICard>
            </Col>

        </Row>
    );
};

// ─── Styled ───────────────────────────────────────────────────────────────────

const CustomHolidayCalendar = styled(Calendar)`
    .back-arrow,
    .next-arrow {
        font-size: 20px;
        cursor: pointer;
        color: ${BaseColor};
    }
    .month-name {
        font-size: 16px;
        font-weight: bold;
        margin: 0 16px;
        text-transform: capitalize;
        color: ${BaseColor};
        display: inline-block;
        width: 160px;
        text-align: center;
    }
    .ant-picker-calendar-mini {
        border-radius: 7px !important;
    }
    .ant-picker-calendar-mini .ant-picker-panel {
        border-radius: 0 0 7px 7px !important;
    }
`;

const DayCell = styled.span<{ $selected?: boolean }>`
    color: ${p => (p.$selected ? BaseColor : "inherit")};
    font-weight: ${p => (p.$selected ? 700 : "normal")};
`;

const StyledCheckbox = styled(Checkbox)`
    margin-top: 8px;
`;

const DetailRow = styled.div`
    display: flex;
    gap: 16px;
    margin-bottom: 8px;
`;

const AllHolidaysRow = styled(Row).attrs({ gutter: [16, 0], align: "middle", wrap: false })`
    padding: 10px 0;
    border-bottom: 1px solid #f0f0f0;
    &:last-child {
        border-bottom: none;
    }
`;

const RecurringBadge = styled.span`
    display: inline-block;
    margin-left: 8px;
    padding: 0 6px;
    border-radius: 4px;
    background: #eef0ff;
    color: ${BaseColor};
    font-size: 11px;
    font-weight: 500;
    vertical-align: middle;
`;
